import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { useTranslation } from 'react-i18next';
import type { MonthlyData, BusinessModelType } from '@/types/financial-model';
import { formatCurrency } from '@/utils/financial-calculations';

interface FinancialModelChartsProps {
  data: MonthlyData[];
  modelType: BusinessModelType;
}

// ---- Colors ----

const BLUE = '#2197f1';
const GREEN = '#27ae60';
const RED = '#e74c3c';
const GRID_COLOR = '#f0f0f0';
const ORANGE = '#f39c12';
const PURPLE = '#9b59b6';

// ---- Abbreviate numbers for YAxis ----

function abbreviateNumber(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${(value / 1_000).toFixed(0)}K`;
  return String(Math.round(value));
}

// ---- Prepare chart data with month labels ----

function prepareChartData(data: MonthlyData[], monthNames: readonly string[]) {
  return data.map((m) => ({
    ...m,
    name: monthNames[m.month - 1],
  }));
}

// ---- Custom Tooltip ----

interface TooltipEntry {
  name: string;
  value: number;
  color: string;
}

function CustomTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: TooltipEntry[];
  label?: string;
}) {
  if (!active || !payload?.length) return null;

  return (
    <div className="bg-white rounded-[8px] border border-[#eeeeee] p-[12px] shadow-sm">
      <p className="font-manrope font-semibold text-[13px] mb-[4px]">{label}</p>
      {payload.map((entry, i) => (
        <p key={i} className="text-[13px]" style={{ color: entry.color }}>
          {entry.name}: {formatCurrency(entry.value)}
        </p>
      ))}
    </div>
  );
}

// ---- Chart Card wrapper ----

function ChartCard({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-white rounded-[16px] border border-[#eeeeee] p-[20px]">
      <h3 className="font-manrope font-semibold text-[16px] text-[#0d0d0d] mb-[12px]">
        {title}
      </h3>
      {children}
    </div>
  );
}

// ---- Chart 1: Revenue & Profit ----

function RevenueAndProfitChart({ chartData, t }: { chartData: ReturnType<typeof prepareChartData>; t: (key: string) => string }) {
  return (
    <ChartCard title={t('chart.revenueAndProfit')}>
      <ResponsiveContainer width="100%" height={280}>
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke={GRID_COLOR} />
          <XAxis
            dataKey="name"
            tick={{ fontSize: 13 }}
            tickLine={false}
            axisLine={{ stroke: GRID_COLOR }}
          />
          <YAxis
            tickFormatter={abbreviateNumber}
            tick={{ fontSize: 13 }}
            tickLine={false}
            axisLine={false}
            width={60}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend wrapperStyle={{ fontSize: '13px' }} />
          <Line
            type="monotone"
            dataKey="revenue"
            name={t('chart.revenue')}
            stroke={BLUE}
            strokeWidth={2}
            dot={{ r: 3 }}
            activeDot={{ r: 5 }}
          />
          <Line
            type="monotone"
            dataKey="netProfit"
            name={t('chart.netProfit')}
            stroke={GREEN}
            strokeWidth={2}
            dot={{ r: 3 }}
            activeDot={{ r: 5 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

// ---- Chart 2: Cash Flow ----

function CashFlowChart({ chartData, t }: { chartData: ReturnType<typeof prepareChartData>; t: (key: string) => string }) {
  const hasNegative = chartData.some((d) => d.cashBalance < 0);
  const gradientId = 'cashFlowGradient';

  return (
    <ChartCard title={t('chart.cashFlow')}>
      <ResponsiveContainer width="100%" height={280}>
        <AreaChart data={chartData}>
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={BLUE} stopOpacity={0.3} />
              <stop offset="100%" stopColor={BLUE} stopOpacity={0.05} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke={GRID_COLOR} />
          <XAxis
            dataKey="name"
            tick={{ fontSize: 13 }}
            tickLine={false}
            axisLine={{ stroke: GRID_COLOR }}
          />
          <YAxis
            tickFormatter={abbreviateNumber}
            tick={{ fontSize: 13 }}
            tickLine={false}
            axisLine={false}
            width={60}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend wrapperStyle={{ fontSize: '13px' }} />
          {hasNegative && (
            <ReferenceLine y={0} stroke="#999" strokeDasharray="3 3" />
          )}
          <Area
            type="monotone"
            dataKey="cashBalance"
            name={t('chart.balance')}
            stroke={BLUE}
            strokeWidth={2}
            fill={`url(#${gradientId})`}
          />
        </AreaChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

// ---- Chart 3: Customer Growth ----

function CustomerGrowthChart({
  chartData,
  isSaas,
  t,
}: {
  chartData: ReturnType<typeof prepareChartData>;
  isSaas: boolean;
  t: (key: string) => string;
}) {
  // For SaaS: show negative churn bars
  const barData = isSaas
    ? chartData.map((d) => ({
        ...d,
        churnedNegative: d.churnedCustomers > 0 ? -d.churnedCustomers : 0,
      }))
    : chartData;

  return (
    <ChartCard title={t('chart.customerGrowth')}>
      <ResponsiveContainer width="100%" height={280}>
        <BarChart data={barData}>
          <CartesianGrid strokeDasharray="3 3" stroke={GRID_COLOR} />
          <XAxis
            dataKey="name"
            tick={{ fontSize: 13 }}
            tickLine={false}
            axisLine={{ stroke: GRID_COLOR }}
          />
          <YAxis
            tickFormatter={abbreviateNumber}
            tick={{ fontSize: 13 }}
            tickLine={false}
            axisLine={false}
            width={60}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend wrapperStyle={{ fontSize: '13px' }} />
          <Bar dataKey="newCustomers" name={t('chart.newCustomers')} fill={BLUE} radius={[4, 4, 0, 0]} />
          {isSaas && (
            <Bar
              dataKey="churnedNegative"
              name={t('chart.churn')}
              fill={RED}
              radius={[0, 0, 4, 4]}
            />
          )}
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

// ---- Chart 4a: Unit Economics (SaaS) ----

function UnitEconomicsTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: TooltipEntry[];
  label?: string;
}) {
  if (!active || !payload?.length) return null;

  return (
    <div className="bg-white rounded-[8px] border border-[#eeeeee] p-[12px] shadow-sm">
      <p className="font-manrope font-semibold text-[13px] mb-[4px]">{label}</p>
      {payload.map((entry, i) => (
        <p key={i} className="text-[13px]" style={{ color: entry.color }}>
          {entry.name}:{' '}
          {entry.name === 'LTV/CAC'
            ? entry.value?.toFixed(2)
            : formatCurrency(entry.value)}
        </p>
      ))}
    </div>
  );
}

function UnitEconomicsChart({ chartData, t }: { chartData: ReturnType<typeof prepareChartData>; t: (key: string) => string }) {
  return (
    <ChartCard title={t('chart.unitEconomics')}>
      <ResponsiveContainer width="100%" height={280}>
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke={GRID_COLOR} />
          <XAxis
            dataKey="name"
            tick={{ fontSize: 13 }}
            tickLine={false}
            axisLine={{ stroke: GRID_COLOR }}
          />
          <YAxis
            yAxisId="left"
            tickFormatter={abbreviateNumber}
            tick={{ fontSize: 13 }}
            tickLine={false}
            axisLine={false}
            width={60}
          />
          <YAxis
            yAxisId="right"
            orientation="right"
            tick={{ fontSize: 13 }}
            tickLine={false}
            axisLine={false}
            width={40}
          />
          <Tooltip content={<UnitEconomicsTooltip />} />
          <Legend wrapperStyle={{ fontSize: '13px' }} />
          <Line
            yAxisId="left"
            type="monotone"
            dataKey="cac"
            name="CAC"
            stroke={RED}
            strokeWidth={2}
            dot={{ r: 3 }}
            activeDot={{ r: 5 }}
            connectNulls
          />
          <Line
            yAxisId="left"
            type="monotone"
            dataKey="ltv"
            name="LTV"
            stroke={GREEN}
            strokeWidth={2}
            dot={{ r: 3 }}
            activeDot={{ r: 5 }}
            connectNulls
          />
          <Line
            yAxisId="right"
            type="monotone"
            dataKey="ltvCacRatio"
            name="LTV/CAC"
            stroke={BLUE}
            strokeWidth={2}
            strokeDasharray="5 5"
            dot={{ r: 2 }}
            connectNulls
          />
        </LineChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

// ---- Chart 4b: Expense Structure (non-SaaS) ----

function ExpenseStructureChart({ chartData, t }: { chartData: ReturnType<typeof prepareChartData>; t: (key: string) => string }) {
  return (
    <ChartCard title={t('chart.expenseStructure')}>
      <ResponsiveContainer width="100%" height={280}>
        <BarChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke={GRID_COLOR} />
          <XAxis
            dataKey="name"
            tick={{ fontSize: 13 }}
            tickLine={false}
            axisLine={{ stroke: GRID_COLOR }}
          />
          <YAxis
            tickFormatter={abbreviateNumber}
            tick={{ fontSize: 13 }}
            tickLine={false}
            axisLine={false}
            width={60}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend wrapperStyle={{ fontSize: '13px' }} />
          <Bar
            dataKey="advertisingCost"
            name={t('chart.advertising')}
            stackId="expenses"
            fill={BLUE}
          />
          <Bar
            dataKey="variableCosts"
            name={t('chart.variable')}
            stackId="expenses"
            fill={ORANGE}
          />
          <Bar
            dataKey="infrastructureCost"
            name={t('chart.infrastructure')}
            stackId="expenses"
            fill={PURPLE}
          />
          <Bar
            dataKey="paymentProcessingCost"
            name={t('chart.commission')}
            stackId="expenses"
            fill={RED}
            radius={[4, 4, 0, 0]}
          />
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

// ---- Main Component ----

export default function FinancialModelCharts({
  data,
  modelType,
}: FinancialModelChartsProps) {
  const { t } = useTranslation('financial-model');
  const MONTH_KEYS = ['jan','feb','mar','apr','may','jun','jul','aug','sep','oct','nov','dec'] as const;
  const monthNames = MONTH_KEYS.map(k => t(`wizard.months.${k}`));
  const chartData = prepareChartData(data, monthNames);
  const isSaas = modelType === 'saas';

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-[16px]">
      <RevenueAndProfitChart chartData={chartData} t={t} />
      <CashFlowChart chartData={chartData} t={t} />
      <CustomerGrowthChart chartData={chartData} isSaas={isSaas} t={t} />
      {isSaas ? (
        <UnitEconomicsChart chartData={chartData} t={t} />
      ) : (
        <ExpenseStructureChart chartData={chartData} t={t} />
      )}
    </div>
  );
}
